import { create } from "zustand";
import {
  deleteData,
  getData,
  postData,
  putData,
} from "../../../../lib/utils/formHelpers";
import { errorAlert } from "../../../utils/alerts";
import { CurvaTypes } from "@app/types/report.types";

const separateAdjustmentCurves = (data: CurvaTypes[]) => {
  const adjustmentCurveToNew: CurvaTypes[] = [];
  const adjustmentCurveToUpdate: CurvaTypes[] = [];

  data.forEach((item) => {
    if (typeof item.id === "string" && item.id.startsWith("new-")) {
      adjustmentCurveToNew.push({ ...item, id: undefined });
    } else {
      adjustmentCurveToUpdate.push(item);
    }
  });

  return { adjustmentCurveToNew, adjustmentCurveToUpdate };
};

const getIdsToDelete = (
  adjustmentCurve: CurvaTypes[],
  adjustmentCurvePreload: CurvaTypes[]
) => {
  // Validar que adjustmentCurvePreload exista y sea un array
  if (!adjustmentCurvePreload || !Array.isArray(adjustmentCurvePreload)) {
    return [];
  }
  
  const currentIds = new Set(adjustmentCurve.map((item) => item.id));
  const preloadIds = adjustmentCurvePreload.map((item) => item.id);

  const idsToDelete = preloadIds.filter((id) => !currentIds.has(id));
  return idsToDelete;
};

type useAdjustmentCurveType = {
  adjustmentCurveList: CurvaTypes[];
  saveAdjustmentCurves: (
    AdjustmentCurve: CurvaTypes[],
    idCurrent: number,
    adjustmentCurvePreload: CurvaTypes[]
  ) => Promise<boolean>;
  fetchPreloadAdjustmentCurve: (id: string) => Promise<CurvaTypes[]>;
  loadingCurve: boolean;
  loadingSave: boolean;
};

export const useAdjustmentCurve = create<useAdjustmentCurveType>(
  (set, get) => ({
    adjustmentCurveList: [],
    loadingCurve: false,
    loadingSave: false,
    fetchPreloadAdjustmentCurve: async (id) => {
      set({ loadingCurve: true });
      try {
        const endpoint = `/generic/AdjustmentCurve/filtered?calibration_tritium=${id}`;
        const { data: response } = await getData(endpoint);

        if (response) {
          set({
            adjustmentCurveList: response,
          });
          return response;
        }
      } catch (error) {
        console.error("Error fetching entities:", error);
        errorAlert("Error al cargar los datos");
      } finally {
        set({ loadingCurve: false });
      }
    },

    saveAdjustmentCurves: async (
      adjustmentCurve,
      idCurrent,
      adjustmentCurvePreload
    ) => {
      set({ loadingSave: true });

      const { adjustmentCurveToNew, adjustmentCurveToUpdate } =
        separateAdjustmentCurves(adjustmentCurve);

      const idsToDelete = getIdsToDelete(
        adjustmentCurve,
        adjustmentCurvePreload
      );
      try {
        for (const newItem of adjustmentCurveToNew) {
          await postData("/generic/AdjustmentCurve", {
            ...newItem,
            calibration_tritium: idCurrent,
          });
        }

        for (const updateItem of adjustmentCurveToUpdate) {
          const { id, ...rest } = updateItem;
          await putData(`/generic/AdjustmentCurve/${id}`, rest);
        }

        for (const deleteItem of idsToDelete) {
          await deleteData(`/generic/AdjustmentCurve/${deleteItem}`);
        }

        return true;
      } catch (error) {
        console.error("Error al guardar los cambios:", error);
        errorAlert("Error al guardar los cambios");
        return false;
      } finally {
        set({ loadingSave: false });
      }
    },
  })
);
